/**
 * Per-outlet view-state keeper for the shell's subtree panels.
 *
 * app-shell renders each subtree panel at a fixed position in its template, so
 * when the user switches to the same subtree of another outlet Lit REUSES the
 * same element and only re-binds `.storeCode` — nothing reloads by itself.
 * (Property bindings do not fire attributeChangedCallback, so panels that only
 * loaded on connect/attribute-change kept showing the previous outlet's search
 * and data under the new outlet's heading.)
 *
 * This cache keeps the screen the user leaves — search text, fetched rows,
 * open detail/edit, active tab, scroll position — keyed by outlet. Switching
 * to an already-visited outlet restores that screen; a first visit starts from
 * a clean state and the caller loads fresh data.
 *
 * States live in a module-level registry per panel scope, so they also survive
 * the element being torn down (switching to another subtree and back). Every
 * snapshot is ALSO persisted to sessionStorage (per browser tab) and re-loaded
 * on demand, so the screens survive a full page reload / PWA relaunch exactly
 * as the user left them — saved on outlet switch, on page hide/background,
 * on panel teardown, and periodically while a view is active.
 * `clearOutletViewCaches()` is called on logout and clears BOTH the memory
 * registry and the persisted snapshots, so the next user starts clean.
 *
 * Async safety: every key switch bumps `epoch`. Loader methods capture the
 * epoch before awaiting and drop the response when `isCurrent(epoch)` is false
 * afterwards — a slow response for the outlet the user just left must never
 * paint into the outlet that is shown now.
 */

import type { LitElement } from 'lit';

export type OutletViewSwitch = 'unchanged' | 'restored' | 'fresh';

export interface OutletViewCacheOptions<S> {
  /** Read the panel's view state (called just before the outlet changes). */
  capture: () => S;
  /** Write a kept view state back into the panel. Force transient flags
   *  (loading/saving spinners) to a resting value inside this callback. */
  restore: (state: S) => void;
  /** Produce the clean slate for a first visit to an outlet. */
  reset: () => void;
}

/** Scroll position memo — element index path from the shadow root plus a
 *  tag/class signature to catch a shifted DOM before applying the position. */
interface ScrollMemo {
  path: number[];
  tag: string;
  cls: string;
  top: number;
}

interface OutletViewSnapshot<S> {
  data: S;
  scrolls: ScrollMemo[];
}

/** scope → outlet key → snapshot. Module-level so kept views survive the
 *  panel element being removed and re-created by the shell. */
const _stores = new Map<string, Map<string, OutletViewSnapshot<unknown>>>();

/** Bumped on clear so live cache instances notice the registry was reset. */
let _generation = 0;

/** sessionStorage key prefix for kept views: `ppos-view:<scope>:<outletKey>`. */
const STORE_PREFIX = 'ppos-view:';

/** Bump when a panel's kept-view shape changes — old snapshots are dropped. */
const SNAPSHOT_VERSION = 1;

/** Minimum gap between periodic checkpoints of the active view. */
const CHECKPOINT_MS = 2500;

interface StoredSnapshot {
  v: number;
  data: unknown;
  scrolls: ScrollMemo[];
}

/** Forget every kept panel view + its persisted snapshot — call on logout. */
export function clearOutletViewCaches(): void {
  _generation++;
  // Clear the per-scope maps THEMSELVES too — live cache instances hold a
  // reference to their scope's map, so dropping the outer entry alone would
  // leave their kept screens restorable.
  for (const kept of _stores.values()) kept.clear();
  _stores.clear();
  try {
    const doomed: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(STORE_PREFIX)) doomed.push(k);
    }
    for (const k of doomed) sessionStorage.removeItem(k);
  } catch {
    // Storage unavailable (private-mode restrictions) — memory was cleared.
  }
}

/**
 * Drop one kept panel view (memory + persisted snapshot) so the next
 * activation starts fresh and refetches. Live server events use this to make
 * sure a list that was closed when the change arrived does not restore stale
 * rows when the user opens it again.
 */
export function invalidateOutletView(scope: string, key: string): void {
  if (!scope || !key) return;
  _stores.get(scope)?.delete(key);
  try {
    sessionStorage.removeItem(`${STORE_PREFIX}${scope}:${key}`);
  } catch {
    // Storage unavailable (private-mode restrictions) — memory was dropped.
  }
}

const MAX_KEPT_OUTLETS = 12;

export class OutletViewCache<S> {
  private readonly _kept: Map<string, OutletViewSnapshot<S>>;
  private readonly _scope: string;
  private _activeKey = '';
  private _epoch = 0;
  private _generation: number;
  private _lastSavedAt = 0;
  private _disposed = false;

  constructor(
    private readonly _host: LitElement,
    scope: string,
    private readonly _opts: OutletViewCacheOptions<S>,
  ) {
    this._scope = scope;
    this._generation = _generation;
    let kept = _stores.get(scope) as Map<string, OutletViewSnapshot<S>> | undefined;
    if (!kept) {
      kept = new Map();
      _stores.set(scope, kept as Map<string, OutletViewSnapshot<unknown>>);
    }
    this._kept = kept;
    // Checkpoint the active screen when the page is reloaded/closed or the app
    // is backgrounded — a killed mobile PWA may skip unload events entirely.
    window.addEventListener('pagehide', this._onPageHide);
    document.addEventListener('visibilitychange', this._onVisibilityChange);
  }

  /** Freshness token — capture before an async load starts. */
  get epoch(): number {
    return this._epoch;
  }

  /** False once the panel moved to another outlet after `epoch` was captured. */
  isCurrent(epoch: number): boolean {
    return epoch === this._epoch;
  }

  /**
   * Activate `key` (the outlet, or `outlet|type` for multi-type panels).
   * Returns `'restored'` when a kept screen was re-drawn, `'fresh'` when the
   * outlet is visited for the first time (the caller should start loading),
   * or `'unchanged'` when the key did not change.
   *
   * Must be called from `updated()` — property bindings never fire
   * attributeChangedCallback, so there is no other reliable hook.
   */
  switchTo(key: string): OutletViewSwitch {
    if (this._generation !== _generation) {
      // The registry was cleared (logout) while this panel stayed alive —
      // drop the local position and any in-flight loads.
      this._generation = _generation;
      this._activeKey = '';
      this._epoch++;
    }
    if (!key) return 'unchanged';
    if (key === this._activeKey) {
      // Periodic checkpoint so a reload/relaunch (or a killed PWA) still has
      // a recent snapshot of the active screen.
      if (this._activeKey && Date.now() - this._lastSavedAt > CHECKPOINT_MS) {
        this._save(this._activeKey);
      }
      return 'unchanged';
    }
    const previous = this._activeKey;
    this._activeKey = key;
    this._epoch++;
    // Save the screen being left BEFORE restore()/reset() mutate anything.
    if (previous) this._save(previous);
    const kept = this._kept.get(key) ?? this._loadPersisted(key);
    if (kept) {
      this._opts.restore(kept.data);
      restoreScrollsSoon(this._host, kept.scrolls);
      return 'restored';
    }
    this._opts.reset();
    return 'fresh';
  }

  /** Final save + detach page listeners — call from the panel's
   *  `disconnectedCallback`. Skips saving when the registry was just cleared
   *  (logout) so the next user can never see this screen. */
  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;
    window.removeEventListener('pagehide', this._onPageHide);
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    if (this._generation === _generation && this._activeKey) this._saveActive();
  }

  private _onPageHide = () => {
    if (this._generation === _generation && this._activeKey) this._saveActive();
  };

  private _onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') this._onPageHide();
  };

  /** Snapshot the active screen (memory + sessionStorage). */
  private _saveActive(): void {
    if (!this._activeKey) return;
    this._save(this._activeKey);
  }

  private _save(key: string): void {
    const snapshot: OutletViewSnapshot<S> = {
      data: this._opts.capture(),
      scrolls: captureScrolls(this._host),
    };
    // Delete + set to refresh the eviction order.
    this._kept.delete(key);
    this._kept.set(key, snapshot);
    this._persist(key, snapshot);
    this._lastSavedAt = Date.now();
    while (this._kept.size > MAX_KEPT_OUTLETS) {
      const oldest = this._kept.keys().next().value as string | undefined;
      if (oldest === undefined) break;
      this._kept.delete(oldest);
      this._removePersisted(oldest);
    }
  }

  private _storageKey(key: string): string {
    return `${STORE_PREFIX}${this._scope}:${key}`;
  }

  private _persist(key: string, snapshot: OutletViewSnapshot<S>): void {
    try {
      const stored: StoredSnapshot = {
        v: SNAPSHOT_VERSION,
        data: snapshot.data,
        scrolls: snapshot.scrolls,
      };
      sessionStorage.setItem(this._storageKey(key), JSON.stringify(stored));
    } catch {
      // QuotaExceeded / storage unavailable — the in-memory keep still works.
    }
  }

  private _loadPersisted(key: string): OutletViewSnapshot<S> | undefined {
    const rawKey = this._storageKey(key);
    try {
      const raw = sessionStorage.getItem(rawKey);
      if (!raw) return undefined;
      const stored = JSON.parse(raw) as StoredSnapshot;
      if (!stored || stored.v !== SNAPSHOT_VERSION || stored.data === undefined) {
        sessionStorage.removeItem(rawKey);
        return undefined;
      }
      const snapshot: OutletViewSnapshot<S> = {
        data: stored.data as S,
        scrolls: stored.scrolls ?? [],
      };
      this._kept.set(key, snapshot);
      return snapshot;
    } catch {
      // Corrupted / unreadable entry — drop it, start fresh.
      try {
        sessionStorage.removeItem(rawKey);
      } catch {
        // ignore
      }
      return undefined;
    }
  }

  private _removePersisted(key: string): void {
    try {
      sessionStorage.removeItem(this._storageKey(key));
    } catch {
      // ignore
    }
  }
}

// ── Scroll position helpers ─────────────────────────────────────────────
// The kept view re-renders the same DOM shape, so a captured element index
// path from the shadow root still addresses the same scroll container.

function captureScrolls(host: LitElement): ScrollMemo[] {
  const root = host.shadowRoot;
  const out: ScrollMemo[] = [];
  if (!root) return out;
  const walk = (node: ParentNode, path: number[]) => {
    const children = node.children;
    for (let i = 0; i < children.length; i++) {
      const el = children[i];
      if (el.scrollTop > 0) {
        out.push({
          path: [...path, i],
          tag: el.tagName,
          cls: typeof el.className === 'string' ? el.className : '',
          top: el.scrollTop,
        });
      }
      if (el.children.length > 0) walk(el, [...path, i]);
    }
  };
  walk(root, []);
  return out;
}

function restoreScrollsSoon(host: LitElement, scrolls: ScrollMemo[]): void {
  if (scrolls.length === 0) return;
  // Wait out the update that re-draws the restored view (the restore happens
  // during `updated()`, so a second wait is needed for its render to land).
  void host.updateComplete
    .then(() => host.updateComplete)
    .then(() => {
      const root = host.shadowRoot;
      if (!root) return;
      for (const memo of scrolls) {
        let node: ParentNode | null = root;
        for (const index of memo.path) {
          node = node ? node.children[index] ?? null : null;
          if (!node) break;
        }
        // The path must still point at the same element — if the restored DOM
        // shifted (e.g. a loading row disappeared), skip rather than scroll
        // the wrong container.
        if (!(node instanceof Element)) continue;
        if (node.tagName !== memo.tag || node.className !== memo.cls) continue;
        node.scrollTop = memo.top;
      }
    });
}
